import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, MenuItem, TextField, Grid } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { getApiErrorMessage } from '../../utilities/helper';
import { RootState } from '../../store';
import { APIResponse, User } from '../../utilities/types';
import CustomAlert from '../elements/custom-alert.element';
import { passwordRegexConstant, phoneRegexConstant } from '../../constants';
import { addUserApi } from '../../apis/admin';
import { getCitiesAPI, getStatesAPI } from '../../apis';

const validationKeys: any = {
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email').notRequired(),
  mobile_number: Yup.string()
    .matches(phoneRegexConstant, 'Phone must be 10 digits')
    .required('Phone number is required'),

  state_id: Yup.number().integer('State id must be an integer').notRequired(),

  city_id: Yup.number().integer('City id must be an integer').notRequired(),

  password: Yup.string().when('id', {
    is: (id: unknown) => !id, // if id does NOT exist → creation
    then: (schema) =>
      schema
        .required('Password is required')
        .min(6, 'Password must be at least 6 characters long')
        .matches(passwordRegexConstant, 'Password must contain at least one letter and one number'),
    otherwise: (schema) =>
      schema
        .notRequired()
        .min(6, 'Password must be at least 6 characters long')
        .matches(passwordRegexConstant, 'Password must contain at least one letter and one number'),
  }),

  confirm_password: Yup.string().when('id', {
    is: (id: unknown) => !id, // no id = creation
    then: (schema) =>
      schema
        .required('Confirm password is required')
        .oneOf([Yup.ref('password')], 'Passwords must match'),
    otherwise: (schema) =>
      schema
        .notRequired()
        .oneOf([Yup.ref('password')], 'Passwords must match if password is provided'),
  }),
};

const defaultInitialValues = {
  id: 0,
  name: '',
  email: '',
  mobile_number: '',
  is_active: '',
  is_verified: '',
  is_mobile_verified: true,
  user_type: '',
  admin_role: '',
  password: '',
  confirm_password: '',
  company_name: '',
  gst_number: '',
  address: '',
  state_id: '',
  city_id: '',
};

interface UserFormProps {
  user: User | null;
  userType: 'admin' | 'seller' | 'buyer';
  onSuccess?: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ user, onSuccess, userType }) => {
  const { systemConfig } = useSelector((state: RootState) => state.common);

  const [initialValues, setInitialValues] = useState<User>({ ...defaultInitialValues });
  const [saveApiError, setSaveApiError] = useState<string | null>(null);
  const [cities, setCities] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);

  const saveUser = useCallback(
    async (values: Partial<User>) => {
      setSaveApiError(null);
      try {
        const payload = {
          ...values,
          user_type: userType,
        };
        const response = (await addUserApi(payload)) as APIResponse<User>;
        if (response.status) {
          onSuccess?.();
        }
      } catch (err) {
        setSaveApiError(getApiErrorMessage(err));
      }
    },
    [onSuccess, userType]
  );

  const getCities = useCallback((state_id: number) => {
    getCitiesAPI({ filters: { state_id } })
      .then((response: any) => {
        if (response.status) {
          setCities(response?.data.items || []);
        }
      })
      .catch((error) => {
        console.log('===========Get cities error', error);
      });
  }, []);

  useEffect(() => {
    if (user) {
      setInitialValues(user);
      getCities(user.state_id);
    }
  }, [user, getCities]);

  useEffect(() => {
    getStatesAPI({})
      .then((response: any) => {
        setStates(response?.data.items || []);
      })
      .catch((error) => {
        console.log('===========Get states error', error);
      });
  }, []);

  if (userType === 'admin') {
    validationKeys.admin_role = Yup.string().required('Role is required');
  }

  const validationSchema = Yup.object(validationKeys);

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={(values) => {
          console.log('Form Submitted ✅', values);
          saveUser(values);
        }}
      >
        {({ values, errors, touched, handleChange, isValid, dirty, setFieldValue }) => (
          <Form>
            {saveApiError && (
              <Box sx={{ my: 1 }}>
                {saveApiError && <CustomAlert type="error">{saveApiError}</CustomAlert>}
              </Box>
            )}
            <Field
              as={TextField}
              fullWidth
              name="name"
              label="Name *"
              margin="normal"
              value={values.name}
              onChange={handleChange}
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
            />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  fullWidth
                  name="mobile_number"
                  label="Mobile Number"
                  margin="normal"
                  value={values.mobile_number}
                  onChange={handleChange}
                  error={touched.mobile_number && Boolean(errors.mobile_number)}
                  helperText={touched.mobile_number && errors.mobile_number}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  fullWidth
                  name="email"
                  label="Email (Optional)"
                  margin="normal"
                  value={values.email}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Grid>
            </Grid>
            {!values.id && (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    margin="normal"
                    value={values.password}
                    onChange={handleChange}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ position: 'relative' }}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="confirm_password"
                      label="Confirm Password"
                      margin="normal"
                      value={values.confirm_password}
                      error={touched.confirm_password && Boolean(errors.confirm_password)}
                      helperText={touched.confirm_password && errors.confirm_password}
                    />
                  </Box>
                </Grid>
              </Grid>
            )}

            {userType === 'admin' && (
              <Field
                as={TextField}
                select
                fullWidth
                name="admin_role"
                label="Role"
                margin="normal"
                value={values.admin_role}
                onChange={handleChange}
              >
                <MenuItem value="">Select</MenuItem>
                {(Object.entries(systemConfig?.admin_roles || {}) as [string, string][]).map(
                  ([key, label]: [string, string]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  )
                )}
              </Field>
            )}
            {userType !== 'admin' && (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="company_name"
                    label="Company Name"
                    margin="normal"
                    value={values.company_name}
                    onChange={handleChange}
                    error={touched.company_name && Boolean(errors.company_name)}
                    helperText={touched.company_name && errors.company_name}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="gst_number"
                    label="GST Number"
                    margin="normal"
                    value={values.gst_number}
                    onChange={handleChange}
                    error={touched.gst_number && Boolean(errors.gst_number)}
                    helperText={touched.gst_number && errors.gst_number}
                  />
                </Grid>
              </Grid>
            )}

            <Field
              as={TextField}
              fullWidth
              rows={2}
              multiline
              name="address"
              label="Address (Optional)"
              margin="normal"
              value={values.address}
              onChange={handleChange}
              error={touched.address && Boolean(errors.address)}
              helperText={touched.address && errors.address}
            />

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  select
                  fullWidth
                  name="state_id"
                  label="State"
                  margin="normal"
                  value={values.state_id}
                  onChange={(e: any) => {
                    handleChange(e);
                    getCities(e.target.value);
                    setFieldValue('city_id', null); // reset city when state changes
                  }}
                >
                  <MenuItem value="">Select State</MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  select
                  fullWidth
                  name="city_id"
                  label="City"
                  margin="normal"
                  value={values.city_id}
                  onChange={handleChange}
                >
                  <MenuItem value="">Select City</MenuItem>
                  {cities.map((city, i) => (
                    <MenuItem key={i} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  select
                  fullWidth
                  name="is_verified"
                  label="Is Verified"
                  margin="normal"
                  value={values.is_verified ? '1' : '0'}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="1">Yes</MenuItem>
                  <MenuItem value="0">No</MenuItem>
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  select
                  fullWidth
                  name="is_active"
                  label="Is Active"
                  margin="normal"
                  value={values.is_active ? '1' : '0'}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="1">Yes</MenuItem>
                  <MenuItem value="0">No</MenuItem>
                </Field>
              </Grid>
            </Grid>
            {/* Submit */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 3 }}
              disabled={!isValid || !dirty}
            >
              Submit
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default UserForm;
